<div class="sample-container" *ngIf="!!selectedSample" role="region"
  [attr.aria-label]="'iaa.app.plasterSample.sampleDetails' | translate">

  <div class="body-wrapper">
    <!-- Each row is a description list, so screen readers pair every label with its value. -->
    <div class="grid-table details">
      <dl class="details-row">
        <div class="detail-item-container">
          <dt class="cutting-item-label">{{ 'iaa.general.excavation' | translate }}</dt>
          <dd class="detail-item-value">
            <ng-container *ngIf="selectedSample.excavations?.length; else plainExcavation">
              <!-- The separator stays outside the button so it isn't read as part of the link name. -->
              <ng-container *ngFor="let excavation of selectedSample.excavations; let last = last">
                <button class="entity-link" type="button" (click)="goTo(excavation.excavationEntityId, 'excavation')">
                  {{ (excavation.name | translateLang | async) || (selectedSample.excavationName | translateLang | async) }}
                </button>{{ !last ? ', ' : '' }}
              </ng-container>
            </ng-container>
            <ng-template #plainExcavation>{{ selectedSample.excavationName | translateLang | async }}</ng-template>
          </dd>
        </div>
        <div class="detail-item-container">
          <dt class="cutting-item-label">{{ 'iaa.general.license' | translate }}</dt>
          <dd class="detail-item-value">{{ getLicenseNames(selectedSample.licenses) }}</dd>
        </div>
        <div class="detail-item-container">
          <dt class="cutting-item-label">{{ 'iaa.app.plasterSample.locus' | translate }}</dt>
          <dd class="detail-item-value">
            <ng-container *ngIf="selectedSample.loci?.length; else plainLocus">
              <ng-container *ngFor="let locus of selectedSample.loci; let last = last">
                <button class="entity-link" type="button" (click)="goTo(locus.locusId, 'locus')">
                  {{ locus.locusName || selectedSample.locusNumber }}
                </button>{{ !last ? ', ' : '' }}
              </ng-container>
            </ng-container>
            <ng-template #plainLocus>{{ selectedSample.locusNumber }}</ng-template>
          </dd>
        </div>
        <div class="detail-item-container">
          <dt class="cutting-item-label">{{ 'iaa.app.plasterSample.basket' | translate }}</dt>
          <dd class="detail-item-value">
            <ng-container *ngIf="selectedSample.baskets?.length; else plainBasket">
              <ng-container *ngFor="let basket of selectedSample.baskets; let last = last">
                <button class="entity-link" type="button" (click)="goTo(basket.basketId, 'basket')">
                  {{ basket.basketName || selectedSample.basketNumber }}
                </button>{{ !last ? ', ' : '' }}
              </ng-container>
            </ng-container>
            <ng-template #plainBasket>{{ selectedSample.basketNumber }}</ng-template>
          </dd>
        </div>
      </dl>

      <dl class="details-row">
        <div class="detail-item-container wide">
          <dt class="cutting-item-label">{{ 'iaa.app.plasterSample.declaredSites' | translate }}</dt>
          <dd class="detail-item-value">
            <!-- A long list folds into a closed panel; a short one stays inline. -->
            <details class="sites-panel" *ngIf="sitesCount > maxInlineSites; else inlineSites">
              <summary>
                <span>{{ 'iaa.app.plasterSample.declaredSitesCount' | translate: { count: sitesCount } }}</span>
                <i class="pi pi-angle-down sites-chevron" aria-hidden="true"></i>
              </summary>
              <ul class="sites-list">
                <li *ngFor="let hachraza of selectedSample.hachrazot">
                  <ng-container *ngTemplateOutlet="siteButton; context: { $implicit: hachraza }"></ng-container>
                </li>
              </ul>
            </details>

            <ng-template #inlineSites>
              <ng-container *ngFor="let hachraza of selectedSample.hachrazot; let last = last">
                <ng-container *ngTemplateOutlet="siteButton; context: { $implicit: hachraza }"></ng-container>{{ !last ? ', ' : '' }}
              </ng-container>
            </ng-template>

            <ng-template #siteButton let-hachraza>
              <button class="entity-link" type="button" (click)="goTo(hachraza.hachrazaEntityId, 'hachraza')">
                <ng-container *ngIf="(hachraza.name | translateLang | async) as siteName; else siteIdOnly">
                  {{ siteName }} ({{ hachraza.hachrazaId }})</ng-container>
                <ng-template #siteIdOnly>{{ hachraza.hachrazaId }}</ng-template>
              </button>
            </ng-template>
          </dd>
        </div>
      </dl>

      <dl class="details-row">
        <div class="detail-item-container wide">
          <dt class="cutting-item-label">{{ 'iaa.general.period' | translate }}</dt>
          <dd class="detail-item-value">{{ selectedSample.archaeologicalPeriod | translateLang | async }}</dd>
        </div>
        <div class="detail-item-container wide">
          <dt class="cutting-item-label">{{ 'iaa.app.plasterSample.context' | translate }}</dt>
          <dd class="detail-item-value">{{ selectedSample.context | translateLang | async }}</dd>
        </div>
        <!-- Previous use is a previous context use, so it sits next to the context. -->
        <div class="detail-item-container wide">
          <dt class="cutting-item-label">{{ 'iaa.app.plasterSample.previousUse' | translate }}</dt>
          <dd class="detail-item-value">{{ selectedSample.previousUse | translateLang | async }}</dd>
        </div>
      </dl>

      <dl class="details-row">
        <div class="detail-item-container wide">
          <dt class="cutting-item-label">{{ 'iaa.app.plasterSample.materialType' | translate }}</dt>
          <dd class="detail-item-value">{{ selectedSample.materialType | translateLang | async }}</dd>
        </div>
        <div class="detail-item-container wide">
          <dt class="cutting-item-label">{{ 'iaa.app.plasterSample.samplingArea' | translate }}</dt>
          <dd class="detail-item-value">{{ selectedSample.samplingArea | translateLang | async }}</dd>
        </div>
      </dl>

      <!-- One row per kind of note; empty ones are skipped. -->
      <ng-container *ngFor="let note of notes; let last = last">
        <dl class="details-row" [class.last]="last">
          <div class="detail-item-container full">
            <dt class="cutting-item-label">{{ note.labelKey | translate }}</dt>
            <dd class="detail-item-value">{{ note.value | translateLang | async }}</dd>
          </div>
        </dl>
      </ng-container>
    </div>

    <!-- Last in the DOM so the photos land at the flex end: left in Hebrew, right in English. -->
    <!-- The sample title goes into the alt text the gallery falls back to when a photo has no description. -->
    <div class="photos" *ngIf="{ title: selectedSample.sampleTitle | translateLang | async } as sample">
      <figure class="photo-card" *ngIf="samplePhoto.length">
        <app-image-gallery [images]="samplePhoto" [thumbnails]="samplePhoto"
          [objectName]="photoAltName('iaa.app.plasterSample.samplePhoto' | translate, sample.title)" />
        <figcaption class="photo-caption">{{ 'iaa.app.plasterSample.samplePhoto' | translate }}</figcaption>
      </figure>
      <figure class="photo-card" *ngIf="microscopePhoto.length">
        <!-- The backend fills this photo's description with the sample's context, which doesn't describe the image. -->
        <app-image-gallery [images]="microscopePhoto" [thumbnails]="microscopePhoto" [altFromDescription]="false"
          [objectName]="photoAltName('iaa.app.plasterSample.microscopePhoto' | translate, sample.title)" />
        <figcaption class="photo-caption">{{ 'iaa.app.plasterSample.microscopePhoto' | translate }}</figcaption>
      </figure>
    </div>
  </div>

  <div class="layers-section flex flex-column w-full">
    <!-- Native details/summary already exposes the expanded state; no role or aria-expanded needed. -->
    <details [open]="layersCount > 0">
      <summary>
        <div class="layers-summary">
          <h3 class="layers-title" id="plaster-sample-layers-title">
            {{ ('iaa.app.plasterSample.layersInSample' | translate) + ' (' + layersCount + ')' }}
          </h3>
          <div class="icons" aria-hidden="true">
            <i [ngClass]="{'pi pi-angle-left icon-default': true, 'en': (currentLanguage | async) === 'en'}"></i>
            <i class="pi pi-angle-down icon-clicked"></i>
          </div>
        </div>
      </summary>

      <table class="layers-table" *ngIf="layersCount > 0" aria-labelledby="plaster-sample-layers-title">
        <thead>
          <tr>
            <th scope="col">{{ 'iaa.app.plasterSample.layerNumber' | translate }}</th>
            <th scope="col">{{ 'iaa.app.plasterSample.layerPosition' | translate }}</th>
            <th scope="col">{{ 'iaa.app.plasterSample.layerType' | translate }}</th>
            <th scope="col">{{ 'iaa.app.plasterSample.composition' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let layer of selectedSample.layers">
            <td>{{ layer.layerNumber }}</td>
            <td>
              <ng-container *ngIf="getLayerPositionKey(layer.layerType) as positionKey">{{ positionKey | translate }}</ng-container>
            </td>
            <td>{{ layer.layerKind }}</td>
            <td>{{ getCompositions(layer.compositions) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="layers-empty" *ngIf="layersCount === 0">
        {{ 'iaa.app.plasterSample.noLayers' | translate }}
      </div>
    </details>
  </div>
</div>
